import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduGrievanceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { ShieldAlert, Plus, Eye, Loader2, Inbox, AlertTriangle, CheckCircle2, Search, ArrowUpRight, UserCheck } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const CATEGORIES = ['faculty', 'staff', 'student', 'POSH', 'academic', 'ethics', 'whistleblower'];
const CATEGORY_LABEL = {
  faculty: 'Faculty', staff: 'Staff', student: 'Student', POSH: 'POSH',
  academic: 'Academic', ethics: 'Ethics', whistleblower: 'Whistleblower',
};
const SEVERITIES = ['low', 'medium', 'high', 'critical'];
const SEVERITY_LABEL = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
const SEVERITY_VARIANT = { low: 'secondary', medium: 'secondary', high: 'default', critical: 'destructive' };

const STATUS_LABEL = {
  submitted: 'Submitted', assigned: 'Assigned', investigating: 'Investigating',
  resolved: 'Resolved', closed: 'Closed',
};
const STATUS_VARIANT = {
  submitted: 'secondary', assigned: 'default', investigating: 'default',
  resolved: 'outline', closed: 'outline',
};

const EMPTY_FORM = {
  title: '', description: '', category: 'faculty', severity: 'medium', is_anonymous: false,
};

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const EducationGrievanceManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [grievances, setGrievances] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [detail, setDetail] = useState(null);

  const [trackToken, setTrackToken] = useState('');
  const [trackResult, setTrackResult] = useState(null);

  const loadGrievances = async () => {
    if (!canManage) { setGrievances([]); return; }
    setLoading(true);
    try {
      const res = await eduGrievanceAPI.list();
      setGrievances(toArray(res));
    } catch (err) {
      // 402 → add-on not active; surface a clear, non-alarming message.
      if (err?.response?.status === 402 || err?.response?.status === 403) {
        setGrievances([]);
      } else {
        Swal.fire('Could not load grievances', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGrievances(); }, [canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const total = grievances.length;
    const open = grievances.filter((g) => !['resolved', 'closed'].includes(g.status)).length;
    const critical = grievances.filter((g) => g.severity === 'critical').length;
    const resolved = grievances.filter((g) => ['resolved', 'closed'].includes(g.status)).length;
    return { total, open, critical, resolved };
  }, [grievances]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const openCreate = () => { setForm(EMPTY_FORM); setDialogOpen(true); };

  const submit = async () => {
    if (!form.title?.trim()) return Swal.fire('Title required', 'Enter a short title for the grievance.', 'warning');
    try {
      const res = await eduGrievanceAPI.create({
        title: form.title.trim(),
        description: form.description || '',
        category: form.category,
        severity: form.severity,
        is_anonymous: Boolean(form.is_anonymous),
      });
      setDialogOpen(false);
      await loadGrievances();
      const token = res?.data?.public_token;
      Swal.fire({
        icon: 'success',
        title: 'Grievance submitted',
        html: token ? `Save your tracking token:<br/><code style="font-weight:600">${token}</code>` : undefined,
      });
    } catch (err) {
      Swal.fire('Submission failed', err?.response?.data?.detail || 'Please review and try again.', 'error');
    }
  };

  const openDetail = async (row) => {
    try {
      const res = await eduGrievanceAPI.get(row.id);
      setDetail(res.data);
    } catch (err) {
      Swal.fire('Could not open grievance', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const runAction = async (fn, id, label) => {
    const { value: note } = await Swal.fire({
      title: label, input: 'text', inputLabel: 'Note (optional)',
      showCancelButton: true, confirmButtonText: label,
    });
    if (note === undefined) return; // cancelled
    try {
      const res = await fn(id, { note: note || undefined });
      setDetail(res.data);
      await loadGrievances();
      Swal.fire({ icon: 'success', title: 'Done', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Action failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const track = async () => {
    const token = trackToken.trim();
    if (!token) return;
    setTrackResult(null);
    try {
      const res = await eduGrievanceAPI.track(token);
      setTrackResult(res.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setTrackResult({ notFound: true });
      } else {
        Swal.fire('Lookup failed', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-indigo-600" /> Grievance Management
          </h2>
          <p className="text-sm text-muted-foreground">
            Confidential intake and redressal — lodge grievances (optionally anonymously), track them by token, and triage through the escalation ladder.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" /> Submit Grievance
        </Button>
      </div>

      {canManage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Grievances" value={stats.total} icon={Inbox} />
          <StatCard label="Open" value={stats.open} icon={AlertTriangle} />
          <StatCard label="Critical" value={stats.critical} icon={ShieldAlert} />
          <StatCard label="Resolved / Closed" value={stats.resolved} icon={CheckCircle2} />
        </div>
      )}

      {/* Token tracking lookup */}
      <Card>
        <CardHeader>
          <CardTitle>Track a Grievance</CardTitle>
          <CardDescription>Look up the current status using the tracking token issued at submission.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1 flex-1 min-w-[220px]">
              <Label>Tracking Token</Label>
              <Input value={trackToken} onChange={(e) => setTrackToken(e.target.value)} placeholder="e.g. Xa7bK-2pQn9" />
            </div>
            <Button variant="outline" onClick={track}><Search className="h-4 w-4 mr-1" /> Track</Button>
          </div>
          {trackResult && (
            <div className="mt-3 text-sm">
              {trackResult.notFound ? (
                <span className="text-muted-foreground">No grievance matches this tracking token.</span>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <code className="font-medium">{trackResult.public_token}</code>
                  <Badge variant={STATUS_VARIANT[trackResult.status] || 'secondary'}>{STATUS_LABEL[trackResult.status] || trackResult.status}</Badge>
                  <span className="text-muted-foreground">Level {trackResult.current_level}</span>
                  {trackResult.category && <span className="text-muted-foreground">· {CATEGORY_LABEL[trackResult.category] || trackResult.category}</span>}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>Grievance Register</CardTitle>
            <CardDescription>All grievances lodged in this institute.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading grievances…
              </div>
            ) : grievances.length === 0 ? (
              <EmptyState
                icon={ShieldAlert}
                title="No grievances yet"
                description="Grievances submitted by members will appear here for triage."
              />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Token</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grievances.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-mono text-xs">{row.public_token}</TableCell>
                        <TableCell className="font-medium">
                          {row.title}
                          {row.is_anonymous && <Badge variant="outline" className="ml-2">Anonymous</Badge>}
                        </TableCell>
                        <TableCell>{CATEGORY_LABEL[row.category] || row.category || '—'}</TableCell>
                        <TableCell><Badge variant={SEVERITY_VARIANT[row.severity] || 'secondary'}>{SEVERITY_LABEL[row.severity] || row.severity}</Badge></TableCell>
                        <TableCell><Badge variant={STATUS_VARIANT[row.status] || 'secondary'}>{STATUS_LABEL[row.status] || row.status}</Badge></TableCell>
                        <TableCell>{row.current_level}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="icon" onClick={() => openDetail(row)} title="View"><Eye className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Submit a Grievance</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setField('category', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setField('severity', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{SEVERITY_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </DialogFormGrid>
            <div className="space-y-1">
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="Short summary of the grievance" />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Describe what happened, who was involved, and when."
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_anonymous} onChange={(e) => setField('is_anonymous', e.target.checked)} />
              Submit anonymously (your identity will not be stored)
            </label>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Dialog open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.title}
                  <Badge variant={STATUS_VARIANT[detail.status] || 'secondary'}>{STATUS_LABEL[detail.status] || detail.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="text-sm text-muted-foreground">
                <code>{detail.public_token}</code> · {CATEGORY_LABEL[detail.category] || detail.category || '—'} ·{' '}
                <Badge variant={SEVERITY_VARIANT[detail.severity] || 'secondary'}>{SEVERITY_LABEL[detail.severity] || detail.severity}</Badge> · Level {detail.current_level}
              </div>
              {detail.description && (
                <div className="text-sm mt-2 whitespace-pre-wrap">{detail.description}</div>
              )}
              <div className="text-xs text-muted-foreground mt-1">
                {detail.is_anonymous ? 'Submitted anonymously' : `Submitted by ${detail.submitted_by || '—'}`}
              </div>

              {canManage && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => runAction(eduGrievanceAPI.assign, detail.id, 'Assign')}>
                    <UserCheck className="h-4 w-4 mr-1" /> Assign
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => runAction(eduGrievanceAPI.escalate, detail.id, 'Escalate')}>
                    <ArrowUpRight className="h-4 w-4 mr-1" /> Escalate
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => runAction(eduGrievanceAPI.resolve, detail.id, 'Resolve')}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Resolve
                  </Button>
                </div>
              )}

              <div className="mt-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Timeline</div>
                {(detail.events || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No events recorded.</div>
                ) : (
                  <ol className="space-y-2">
                    {detail.events.map((e) => (
                      <li key={e.id} className="text-sm border-l-2 border-muted pl-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{e.action}</Badge>
                          <span className="text-muted-foreground">Level {e.level}</span>
                          <span className="text-xs text-muted-foreground">{e.occurred_at ? new Date(e.occurred_at).toLocaleString() : ''}</span>
                        </div>
                        <div className="text-muted-foreground">{e.actor || 'anonymous'}{e.note ? ` — ${e.note}` : ''}</div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div className="mt-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Attachments</div>
                {(detail.attachments || []).length === 0
                  ? <div className="text-sm text-muted-foreground">None.</div>
                  : <div className="flex flex-wrap gap-2">{detail.attachments.map((a) => (
                      <Badge key={a.id} variant="outline">{a.filename}</Badge>
                    ))}</div>}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationGrievanceManagement;
